import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import CustomModal from "../../../packages/CustomModal";

import ChemicalLibraryTable from "../ChemicalLibraryTable/ChemicalLibraryTable";
import ChemicalLibraryAddEditDialog from "../ChemicalLibraryAddEditDialog/ChemicalLibraryAddEditDialog";
import {
  fetchChemicalLibraryData,
  updateChemicalLibraryData,
} from "./ChemicalLibraryDialog.api";
import WPMessageBanner, {
  BANNER_TYPES,
} from "../../../packages/WPMessageBanner/WPMessageBanner";
import {
  PENDING_PLANT_CHEMICAL_LIBRARY_KEY,
  CustomModalButtons,
  FEATURE_SCOPE,
} from "../../../constants/global.constants";
import { extractPlantId } from "../../../utils/Utils";

import "./ChemicalLibraryDialog.scss";
import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../../packages/ConfirmationPopup";
import WPTertiaryButton from "../../../packages/WPTertiaryButton";
import { saveItemSession } from "../../../utils/session";
import createNotification, { SUCCESS_NOTIFICATION } from "../../Notifications";
import {
  storePlantChemicalLibrary,
  storeUserChemicalLibrary,
} from "../../../Store/TemplateStore/template.action";

const ChemicalLibraryDialog = ({ onClose, scope = FEATURE_SCOPE.USER }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isUserScope = scope === FEATURE_SCOPE.USER;

  const [chemicalLibrary, setChemicalLibrary] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [showAddEditDialog, setShowAddEditDialog] = useState(false);
  const [pendingChemicalData, setPendingChemicalData] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [shouldResetData, setShouldResetData] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { userChemicalLibrary, plantChemicalLibrary } = useSelector(
    (state) => state,
  );

  const makeDeafaultBtn = (
    <WPTertiaryButton
      label={t("chemicalLibrary.makeDefault")}
      onClick={() => setShowConfirmation(true)}
      disabled={hasUnsavedChanges}
      variant="text"
      size="small"
    />
  );

  const isMakeDefaultConfirmation = !isUserScope && !hasUnsavedChanges;

  const plantId = extractPlantId();
  const isLocalChanges = !isUserScope && !isMakeDefaultConfirmation && !plantId;

  const mergeChangedChemicalRows = (existingRows = [], updatedRows = []) => {
    const changedRows = Array.isArray(updatedRows) ? updatedRows : [];

    return existingRows.map((item) => {
      const changedItem = changedRows.find(
        (row) => row?.chem_id === item?.chem_id,
      );
      return changedItem ? { ...item, ...changedItem } : item;
    });
  };

  const confirmDialogConfig = isMakeDefaultConfirmation
    ? {
        header: t("makeDefault.confirmHeader"),
        description: t("makeDefault.confirmDescription"),
        confirmBtn: t("makeDefault.setAsDefault"),
        cancelBtn: t("ValueStrings.cancel"),
      }
    : {
        header: t(`${scope}.confirmHeader`),
        description: t(`${scope}.confirmDescription`),
        confirmBtn: t("ValueStrings.save&Update"),
        cancelBtn: t("ValueStrings.dontSave"),
      };

  const upsertPendingChemicalData = (chemicalData) => {
    setPendingChemicalData((prev) => {
      const prevList = Array.isArray(prev) ? prev.filter(Boolean) : [];
      const index = prevList.findIndex(
        (item) => item?.chem_id === chemicalData?.chem_id,
      );

      if (index === -1) {
        return [...prevList, chemicalData];
      }

      const mergedData = [...prevList];
      mergedData[index] = { ...mergedData[index], ...chemicalData };
      return mergedData;
    });
  };

  const openAddEditDialog = (chemicalData) => {
    upsertPendingChemicalData(chemicalData);
    setShowAddEditDialog(true);
    setCurrentAction("editChemical");

    setHasUnsavedChanges(false);
    setHasError(false);
  };

  const closeAddEditDialog = () => {
    setShowAddEditDialog(false);
    setCurrentAction(null);
    setPendingChemicalData([]);
    setHasUnsavedChanges(false);
  };

  const onActionBtnClick = (actionData) => {
    const { actionType, chemicalData } = actionData;
    if (actionType === "edit-chemical-btn") {
      openAddEditDialog(chemicalData);
    }
    // else if (actionType === "delete-chemical-btn") {
    //   // handle delete action
    // }
  };

  const handleChemicalEditSubmit = (newData, hasError) => {
    upsertPendingChemicalData(newData);
    if (isUserScope) {
      setShowConfirmation(true);
    }
    setHasUnsavedChanges(true);
    setShouldResetData(false);
    setHasError(hasError);
  };

  const handleChemicalListDialogClose = ({ target: { id } }) => {
    if (id === CustomModalButtons.CONFIRM) {
      setShowConfirmation(true);
    } else {
      setPendingChemicalData([]);
      onClose();
    }
  };

  const handleConfirmDlgClose = async (action) => {
    setShowConfirmation(false);

    if (action === CustomModalButtons.CONFIRM) {
      setIsLoading(true);
      try {
        const chemicalDataForUpdate = isMakeDefaultConfirmation
          ? chemicalLibrary
          : pendingChemicalData;

        let updatedChemicalLibrary = [];

        if (isLocalChanges) {
          const chemicalPayload = chemicalDataForUpdate.map((item) => ({
            chem_id: item.chem_id,
            price: Number(item.price),
            unit: item.unit,
          }));

          saveItemSession(
            PENDING_PLANT_CHEMICAL_LIBRARY_KEY,
            JSON.stringify({ chemicals: chemicalPayload }),
          );

          createNotification(
            SUCCESS_NOTIFICATION,
            t("chemicalLibrary.dataUpdatedSuccessfully"),
            "",
          );

          updatedChemicalLibrary = mergeChangedChemicalRows(
            chemicalLibrary,
            chemicalDataForUpdate,
          );
        } else {
          updatedChemicalLibrary = await updateChemicalLibraryData(
            chemicalDataForUpdate,
            t,
            isUserScope,
            isMakeDefaultConfirmation,
          );
        }

        setChemicalLibrary(updatedChemicalLibrary || []);

        if (isUserScope) {
          closeAddEditDialog();
          dispatch(storeUserChemicalLibrary(updatedChemicalLibrary));
        } else {
          dispatch(storePlantChemicalLibrary(updatedChemicalLibrary));
          setHasUnsavedChanges(false);
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      setHasUnsavedChanges(false);
      setShouldResetData(true);
      if (!isUserScope) {
        setPendingChemicalData([]);
      }
    }
  };

  useEffect(() => {
    const loadChemicalLibrary = async () => {
      setIsLoading(true);
      const chemicalData = await fetchChemicalLibraryData(t, isUserScope);
      if (isUserScope) {
        dispatch(storeUserChemicalLibrary(chemicalData));
      } else {
        dispatch(storePlantChemicalLibrary(chemicalData));
      }
      setChemicalLibrary(chemicalData || []);
      setIsLoading(false);
    };

    if (isUserScope && userChemicalLibrary && userChemicalLibrary.length > 0) {
      setChemicalLibrary(userChemicalLibrary);
    } else if (
      !isUserScope &&
      plantChemicalLibrary &&
      plantChemicalLibrary.length > 0
    ) {
      setChemicalLibrary(plantChemicalLibrary);
    } else {
      loadChemicalLibrary();
    }
  }, [t, isUserScope]);

  return (
    <>
      <CustomModal
        header={t(`${scope}.chemicalHeader`)}
        size="lg"
        invisible={currentAction}
        isLoading={isLoading}
        hideFooter={isUserScope}
        confirmBtn={t("ValueStrings.saveChanges")}
        onModalClose={handleChemicalListDialogClose}
        confirmBtnDisabled={!hasUnsavedChanges || hasError}
        modalFooter={!isUserScope ? makeDeafaultBtn : null}
      >
        <WPMessageBanner
          type={BANNER_TYPES.info}
          message={t(`${scope}.infoMessage`)}
          className="marginTop0"
        />

        <ChemicalLibraryTable
          chemicalLibrary={chemicalLibrary}
          context={{ onActionBtnClick }}
          scope={scope}
          isLoading={isLoading}
          onSubmit={handleChemicalEditSubmit}
          shouldResetData={shouldResetData}
        />
      </CustomModal>

      {showAddEditDialog && (
        <ChemicalLibraryAddEditDialog
          onClose={closeAddEditDialog}
          chemicalRowData={pendingChemicalData[0]}
          isLoading={isLoading}
          onSubmit={handleChemicalEditSubmit}
          shouldResetData={shouldResetData}
        />
      )}

      {showConfirmation && (
        <ConfirmationPopup
          type={DIALOG_TYPES.WARNING}
          header={confirmDialogConfig.header}
          description={confirmDialogConfig.description}
          confirmBtn={confirmDialogConfig.confirmBtn}
          cancelBtn={confirmDialogConfig.cancelBtn}
          onClose={handleConfirmDlgClose}
        />
      )}
    </>
  );
};

export default ChemicalLibraryDialog;
